/**
 * streamAnalyze – Streaming wrapper for /api/kanai/analyze.
 *
 * Sends the same payload as a normal analyze call but with `stream: true`.
 * Calls `onText(accumulatedText)` as chunks arrive (for live typing UX),
 * then returns the same structured data object as the non-streaming endpoint
 * (answerSections, citations, rawAnswerMarkdown, etc.).
 *
 * Usage:
 *   const data = await streamAnalyze(url, body, {
 *     onText: (text) => setLiveText(text),
 *     credentials: 'include',
 *     headers: { 'X-Tenant-Slug': slug },
 *     signal: abortController.signal,
 *   });
 *   // `data` has .answerSections, .citations, .rawAnswerMarkdown, .elapsedMs etc.
 */
export async function streamAnalyze(url, body, opts = {}) {
  const { onText, signal, credentials, headers: extraHeaders } = opts;

  // Auto-add tenant header (same as apiFetch in api.js)
  const autoHeaders = {};
  try {
    const slug = localStorage.getItem('kanplan-active-tenant-slug');
    if (slug) autoHeaders['X-Tenant-Slug'] = slug;
    const simRole = (localStorage.getItem('kanplan-simulated-role') || '').trim();
    if (simRole) autoHeaders['X-Simulated-Role'] = simRole;
  } catch { /* ignore */ }

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...autoHeaders, ...extraHeaders },
    credentials: credentials || 'include',
    body: JSON.stringify({ ...body, stream: true }),
    signal,
  });

  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throw new Error(errBody.detail || `HTTP ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullText = '';
  let meta = null;
  let streamError = null;
  let stopped = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6));
          switch (data.type) {
            case 'text_delta':
              fullText += data.text;
              if (onText) onText(fullText);
              break;
            case 'stopped':
              // Svaret avbröts på servern (max_tokens, max_rounds, refusal)
              stopped = data.reason || null;
              break;
            case 'done':
              meta = data;
              break;
            case 'error':
              streamError = data.message || 'Ett fel uppstod';
              break;
            default:
              break;
          }
        } catch (_e) { /* malformed JSON */ }
      }
    }
  }

  if (streamError) throw new Error(streamError);

  // Build a response object compatible with the non-streaming /analyze response
  return {
    answerSections: meta?.answerSections || { Svar: [], Källor: [], Osäkerheter: [] },
    rawAnswerMarkdown: meta?.rawAnswerMarkdown || fullText,
    citations: meta?.citations || [],
    structuredFindings: meta?.structuredFindings || [],
    docWarnings: meta?.docWarnings || null,
    elapsedMs: meta?.elapsedMs || 0,
    roleLabel: meta?.roleLabel || null,
    modeId: meta?.modeId || body.modeId,
    provider: meta?.provider || null,
    model: meta?.model || null,
    usage: meta?.usage || null,
    auditLogId: meta?.auditLogId || null,
    // Nivåbyte utan ny sökning: skicka tillbaka som ateranvand_kontext (2026-10-19).
    kontextId: meta?.kontextId || null,
    stopped: meta?.stopped || stopped || null,
    // Keep rawChat compatible
    rawChat: [{ role: 'assistant', content: fullText }],
    // Fallback to streamed prose when backend's done-event saknar answerSections
    // (uppstår i tools-loop-vägen där done_data ej innehåller answerSections).
    // Utan denna fallback visade FAB-widgeten "Inget svar tillgängligt".
    summaryPoints: meta?.answerSections?.Svar?.length
      ? meta.answerSections.Svar
      : (fullText ? [fullText] : []),
    // Full text for components that just need the prose
    _streamedText: fullText,
  };
}
